import styled from "styled-components";
import { useEffect, useState } from "react";
import { useAuthContext } from "../hooks/auth/AuthContext";
import useDebounce from "../hooks/use-debounce";
import { useSettingsContext } from "../hooks/settings/SettingsContext";
import {
  getCategories,
  getTypes,
  getWords,
  postWord,
  putWord,
  deleteWord,
} from "../services/vocabulary";
import TableVocabulary from "../components/tableVocabulary";
import ExerciseVocabulary from "../components/modal/exerciseVocabulary";
import FormVocabulary from "../components/modal/formVocabulary";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import IconButton from "@mui/material/IconButton";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ChecklistIcon from "@mui/icons-material/Checklist";
import AddIcon from "@mui/icons-material/Add";
import PlaylistRemoveIcon from "@mui/icons-material/PlaylistRemove";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";

import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Furigana from "../components/furigana";
import Typography from "@mui/material/Typography";
import Pagination from "@mui/material/Pagination";

const StyledMainBox = styled(Box)`
  height: 100dvh;
  display: flex;
  flex-direction: column;
  padding: 57px 0 0;

  @media (min-width: 641px) {
    padding: 65px 0 0;
  }
`;

const StyledContentBox = styled(Box)`
  height: 100%;
  width: 100%;
  margin: 0;
  padding: 0;
`;

const StyledHeaderBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  padding: 10px;
  box-shadow: 0px 5px 4px black;
`;

const StyledFooterBox = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  height: 50px;
  padding: 10px;
  box-shadow: 0px -5px 4px black;
`;

const StyledProgress = styled.div`
  z-index: 999;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #ffffff3b;
`;

const Vocabulary = () => {
  const { user } = useAuthContext();
  // const { user, isLoading } = useAuthContext();
  const { debounce, setSearch, search } = useDebounce();
  const { furigana, romanji } = useSettingsContext();

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [vocabularyList, setVocabularyList] = useState([]);
  const [filteredVocabulary, setFilteredVocabulary] = useState([]);
  const [practiceList, setPracticeList] = useState([]);
  const [practiceListTemp, setPracticeListTemp] = useState([]);
  const [openExerciceVocabulary, setOpenExerciceVocabulary] = useState(false);
  const [exerciseStep, setExerciseStep] = useState(0);
  const [wordForm, setWordForm] = useState();
  const [openForm, setOpenForm] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [pageSize] = useState(20);
  const [page, setPage] = useState(1);

  const [openPracticeList, setOpenPracticeList] = useState(false);
  const handleOpenPracticeList = () => setOpenPracticeList(true);
  const handleClosePracticeList = () => setOpenPracticeList(false);

  const handleChangePage = (event, value) => setPage(value);

  useEffect(() => {
    getCategories()
      .then(({ data }) => {
        setCategories(data);
        setSelectedCategory(data[0]);
      })
      .catch(() => {});
    getTypes()
      .then(({ data }) => {
        setTypes(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      setLoading(true);
      setPage(1);
      setSearch("");
      getWords({ category: selectedCategory })
        .then(({ data }) => {
          setVocabularyList(data);
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedCategory, setSearch]);

  useEffect(() => {
    setFilteredVocabulary(
      search
        ? vocabularyList.filter(
            ({ japanese, french, selfid }) =>
              japanese.toLowerCase().search(search.toLowerCase()) > -1 ||
              french.filter(
                (f) => f.toLowerCase().search(search.toLowerCase()) > -1,
              ).length > 0 ||
              selfid.toLowerCase().search(search.toLowerCase()) > -1,
          )
        : vocabularyList,
    );
  }, [vocabularyList, search]);

  useEffect(() => {
    let nbPages = Math.ceil(filteredVocabulary.length / pageSize);
    setPageCount(nbPages);
    if (nbPages < page && page > 1) setPage(nbPages);
  }, [filteredVocabulary, pageSize, page]);

  const updatePracticeList = (word) => {
    let myList = [];
    if (practiceList.findIndex((w) => w.selfid === word.selfid) >= 0)
      myList = practiceList.reduce(
        (a, c) => [...a, ...(c.selfid === word.selfid ? [] : [c])],
        [],
      );
    else myList = [...practiceList, word];
    setPracticeList(myList);
  };

  const createVocabulary = async (vocabulary) => {
    console.log(vocabulary);
    return postWord(vocabulary).then(({ data }) => {
      console.log(data);
      setVocabularyList((v) => [...v, data]);
    });
  };

  const updateVocabulary = async (_id, vocabulary) => {
    return putWord(_id, vocabulary).then(({ data }) =>
      setVocabularyList((v) => v.map((v) => (v._id === data._id ? data : v))),
    );
  };

  const removeVocabulary = async (_id) => {
    return deleteWord(_id).then(({ data }) => {
      setVocabularyList((v) => v.filter((v) => v._id !== data._id));
      setPracticeList(
        practiceList.reduce((a, c) => [...a, ...(c === _id ? [] : [c])], []),
      );
    });
  };

  return (
    <StyledMainBox>
      <StyledContentBox>
        {loading ? (
          <StyledProgress>
            <CircularProgress />
          </StyledProgress>
        ) : (
          <>
            <StyledHeaderBox>
              <FormControl
                size="small"
                sx={{
                  width: "170px",
                }}
              >
                <InputLabel>Catégorie</InputLabel>
                {selectedCategory && (
                  <Select
                    label="Catégorie"
                    defaultValue={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {categories.map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </FormControl>
              <IconButton
                variant="outlined"
                onClick={() => {
                  handleOpenPracticeList();
                  setPracticeListTemp(practiceList);
                }}
                disabled={practiceList.length === 0}
                size="small"
              >
                <ChecklistIcon />
              </IconButton>
              <TextField
                label="Rechercher"
                size="small"
                sx={{
                  width: "170px",
                }}
                onChange={({ target }) => debounce(target.value)}
              />
            </StyledHeaderBox>
            <TableVocabulary
              selectedCategory={selectedCategory}
              vocabularyList={filteredVocabulary}
              page={page}
              pageSize={pageSize}
              practiceList={practiceList}
              updatePracticeList={updatePracticeList}
              setWordForm={setWordForm}
              setOpenForm={setOpenForm}
            />
            <StyledFooterBox>
              <IconButton
                variant="outlined"
                onClick={() => {
                  setExerciseStep(0);
                  setOpenExerciceVocabulary(true);
                }}
                disabled={practiceList.length < 5}
                size="small"
              >
                <FitnessCenterIcon />
              </IconButton>
              <Pagination
                count={pageCount}
                page={page}
                onChange={handleChangePage}
                color="primary"
              />
              <IconButton
                variant="outlined"
                onClick={() => {
                  setWordForm();
                  setOpenForm(true);
                }}
                size="small"
                disabled={!user}
              >
                <AddIcon />
              </IconButton>
            </StyledFooterBox>
          </>
        )}
      </StyledContentBox>
      <Dialog
        open={openPracticeList}
        fullWidth
        keepMounted
        onClose={handleClosePracticeList}
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          Vocabulaire sélectionné
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {practiceList.length > 0 ? (
              <IconButton
                variant="outlined"
                onClick={() => setPracticeList([])}
              >
                <PlaylistRemoveIcon />
              </IconButton>
            ) : (
              <IconButton
                variant="outlined"
                onClick={() => setPracticeList(practiceListTemp)}
              >
                <PlaylistAddCheckIcon />
              </IconButton>
            )}
          </DialogContentText>

          {practiceListTemp.map((word, index) => {
            return (
              <Paper
                key={`row${index}`}
                sx={{
                  margin: "5px",
                  padding: "5px",
                  height: "65px",

                  ...(practiceList.indexOf(word) > -1
                    ? {
                        backgroundColor: "#0d6efd50",
                        "&:hover": {
                          backgroundColor: "#0d6efd80",
                        },
                      }
                    : {}),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#ffffff20",
                    borderColor: "#4FC3F7",
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "33%" }}
                >
                  <Checkbox
                    checked={
                      practiceList.findIndex((p) => p.selfid === word.selfid) >=
                      0
                    }
                    onChange={() => updatePracticeList(word)}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Furigana furigana={furigana} center text={word.japanese} />
                    {romanji && (
                      <Typography
                        variant="subtitle2"
                        sx={{ color: "#ffffff80" }}
                      >
                        {word.selfid.split("-")[1]}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    width: "34%",
                  }}
                >
                  {word.french.map((w, index) => (
                    <Typography key={`row${index}`} variant="body2">
                      {w}
                    </Typography>
                  ))}
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    width: "33%",
                  }}
                >
                  {word.category.map((w, index) => (
                    <Typography key={`row${index}`} variant="body2">
                      {w}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePracticeList}>Fermer</Button>
        </DialogActions>
      </Dialog>
      <ExerciseVocabulary
        vocabularyList={vocabularyList}
        practiceList={practiceList}
        open={openExerciceVocabulary}
        setOpen={setOpenExerciceVocabulary}
        step={exerciseStep}
        setStep={setExerciseStep}
      />
      <FormVocabulary
        selected={wordForm}
        open={openForm}
        categories={categories}
        types={types}
        setOpen={setOpenForm}
        create={createVocabulary}
        update={updateVocabulary}
        remove={removeVocabulary}
      />
    </StyledMainBox>
  );
};

export default Vocabulary;
